import { Switch } from "@mui/material";

import { ExplainedControl } from "./ExplainedControl";

/**
 * One preference switch, named for a screen reader and described by
 * {@link ExplainedControl}.
 *
 * `role: "switch"` is repeated in `slotProps.input` because MUI sets it through that
 * same slot, so passing a label without it downgrades the control to a checkbox.
 */

export type PreferenceSwitchProps = {
  /** The switch's accessible name: which alert, and which channel. */
  label: string;
  explanation: string;
  /**
   * The stored position, not the delivered value. Under a mute the two differ, and
   * rendering the delivered value would both lie about what is saved and offer to
   * save that lie back.
   */
  checked: boolean;
  /**
   * Set while a write is in flight. Never used for a switch that is merely muted
   * from above: a muted switch is still the reader's to change, and disabling it
   * would take away the choices the mute exists to preserve.
   */
  disabled?: boolean;
  /** MUI switch size; the per-alert switches are small so they sit inline. */
  size?: "small" | "medium";
  onChange: (enabled: boolean) => void;
};

export function PreferenceSwitch({
  label,
  explanation,
  checked,
  disabled,
  size,
  onChange,
}: PreferenceSwitchProps) {
  return (
    <ExplainedControl explanation={explanation}>
      {(descriptionId) => (
        <Switch
          checked={checked}
          disabled={disabled}
          size={size}
          onChange={(event) => onChange(event.target.checked)}
          slotProps={{
            input: { role: "switch", "aria-label": label, "aria-describedby": descriptionId },
          }}
        />
      )}
    </ExplainedControl>
  );
}
